import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "../../../../lib/googleMaps";
import { getMapsSettings } from "../../../../lib/mapsSettings";
import type { MapBounds } from "../../../../lib/companyMap";
import { currentPalette, isDarkTheme, watchTheme } from "./mapTheme";

/**
 * Estonia's bounding box: Vilsandi in the west, Narva in the east, the southern
 * border, and the north coast with Vaindloo.
 *
 * The map is FITTED to this rather than opened at a fixed centre and zoom, and
 * the difference is the whole point. A fixed zoom is only correct for one pane
 * shape: the same `zoom: 7` that framed the country in a short letterbox pane
 * put half the Gulf of Finland on screen once the map filled the column, and it
 * would be wrong again in fullscreen and wrong a third way on a phone.
 * `fitBounds` asks the question the operator actually means - "show me Estonia"
 * - and lets Google work out the zoom for the box it has been given.
 */
const HOME_BOUNDS = { south: 57.5, west: 21.7, north: 59.7, east: 28.25 };

/** The centre of that box, and a zoom that frames it on a typical desktop pane.
 *  Only the starting position: `fitBounds` corrects it as soon as the map knows
 *  its own size. Without it the map opens on the Atlantic for one frame. */
const HOME = { lat: 58.6, lng: 25.0, zoom: 7 };

/** Breathing room around the country, in pixels, so the coast is not flush
 *  against the edge of the pane. */
const HOME_PADDING = 24;

/**
 * How long the map has to sit still before we ask the server about it.
 *
 * A drag fires `bounds_changed` on every animation frame, so without this a
 * single flick across the country is a hundred queries. Waiting for `idle`
 * alone is not enough either - Google fires that after every inertia step.
 */
const SETTLE_MS = 320;

export type MapPhase = "loading" | "ready" | "no-key" | "failed";

export interface CompanyMapState {
  phase: MapPhase;
  /** The map object, once there is one. Null in every other phase. */
  map: google.maps.Map | null;
  /** The current viewport, after it has settled. */
  view: { bounds: MapBounds; zoom: number } | null;
}

/**
 * Loads Google Maps with the key from CRM settings and hands back a live map.
 *
 * The key is fetched rather than baked in, because it lives in a `Setting` row
 * an operator edits (Settings -> Integrations -> Map). `phase: "no-key"` is a
 * first-class outcome, not an error: a CRM that has not been given a key yet is
 * unconfigured, and the view says so with a link rather than a broken map.
 *
 * ⚠ The Maps script can only be loaded ONCE per page with ONE key. Changing the
 * key therefore needs a reload, which is why the settings panel tests keys in
 * its own iframe instead of using this hook.
 */
export function useCompanyMap(host: React.RefObject<HTMLDivElement | null>): CompanyMapState {
  const [phase, setPhase] = useState<MapPhase>("loading");
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [view, setView] = useState<{ bounds: MapBounds; zoom: number } | null>(null);
  /**
   * Bumped when the CRM's theme flips, which rebuilds the map.
   *
   * ⚠ A rebuild, not a `setOptions`. `colorScheme` is documented as "the INITIAL
   * map color scheme", checked once when the map is constructed, so there is no
   * way to repaint a live map - and the alternative, leaving a light map inside
   * a dark CRM, is the thing being fixed. The viewport is carried across, so the
   * operator keeps the place they were looking at.
   */
  const [themeKey, setThemeKey] = useState(() => (isDarkTheme() ? "dark" : "light"));
  const keepView = useRef<{ center: google.maps.LatLngLiteral; zoom: number } | null>(null);

  useEffect(() => watchTheme((dark) => setThemeKey(dark ? "dark" : "light")), []);

  /**
   * Builds the map, and rebuilds it when the theme flips.
   *
   * ⚠ The teardown lives in THIS effect's cleanup rather than in one of its own.
   * React runs effects in declaration order, so a separate "reset" effect ran
   * AFTER this one on a theme change - the builder had already bailed out on a
   * stale guard, and the map never came back. One effect owning the whole
   * lifecycle cannot get that wrong.
   */
  useEffect(() => {
    let cancelled = false;
    let built: google.maps.Map | null = null;

    void (async () => {
      let key = "";
      try {
        key = (await getMapsSettings()).browserKey;
      } catch {
        if (!cancelled) setPhase("failed");
        return;
      }
      if (cancelled) return;
      if (!key) {
        setPhase("no-key");
        return;
      }

      try {
        await loadGoogleMaps(key);
      } catch {
        if (!cancelled) setPhase("failed");
        return;
      }
      if (cancelled || !host.current) return;

      const palette = currentPalette();
      const instance = new google.maps.Map(host.current, {
        center: keepView.current?.center ?? { lat: HOME.lat, lng: HOME.lng },
        zoom: keepView.current?.zoom ?? HOME.zoom,
        // Google's own dark cartography, which is the one form of styling that
        // still works alongside a map ID (see `mapTheme.ts` for why a custom
        // JSON palette cannot be used here).
        colorScheme: palette.scheme,
        // A working map, not a tourist one: the register's own pins are the
        // subject, and Google's restaurants and bus stops on top of them are
        // noise an operator has to look past.
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        // Required for AdvancedMarkerElement. `DEMO_MAP_ID` is Google's own
        // public id for exactly this - it needs no Cloud console setup, which
        // matters because the operator has already been asked to create a key
        // and should not be sent back for a map style as well.
        mapId: "DEMO_MAP_ID",
      });

      // Fit AFTER construction, not instead of it: `fitBounds` needs the pane to
      // have a size, and at this point the div has just been laid out. Google
      // defers the work itself when it has not, so this is safe either way.
      // Skipped on a theme rebuild, which is carrying a viewport across.
      if (!keepView.current) instance.fitBounds(HOME_BOUNDS, HOME_PADDING);

      built = instance;
      setMap(instance);
      setPhase("ready");
    })();

    return () => {
      cancelled = true;
      if (!built) return;
      // Remember where the operator was looking, so the rebuilt map opens on the
      // same place rather than snapping back to the whole country.
      const centre = built.getCenter();
      const zoom = built.getZoom();
      if (centre && zoom != null) keepView.current = { center: centre.toJSON(), zoom };
      setMap(null);
      setPhase("loading");
    };
  }, [host, themeKey]);

  // Viewport tracking, debounced. Reports only after the map has settled, so a
  // drag across the country is one query rather than one per frame.
  useEffect(() => {
    if (!map) return;
    let timer: number | undefined;

    const settle = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const b = map.getBounds();
        const zoom = map.getZoom();
        if (!b || zoom == null) return;
        const ne = b.getNorthEast();
        const sw = b.getSouthWest();
        setView({
          bounds: { north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() },
          zoom,
        });
      }, SETTLE_MS);
    };

    const listener = map.addListener("idle", settle);
    settle();

    return () => {
      window.clearTimeout(timer);
      listener.remove();
    };
  }, [map]);

  return { phase, map, view };
}

export const MAP_HOME = HOME;
export const MAP_HOME_BOUNDS = HOME_BOUNDS;
